import { MISSING_IMAGE } from '@/constants/image.const'
import { RETURN_ELIGIBILITY_MESSAGES } from '@/constants/appReturn.const'
import { CUSTOMER_RETURN_CONFIG } from '@/config/customerReturn.config'
import { getReturnEligibilityMessage } from '@/utils/customerReturnEligibility.util'

/**
 * Customer Return Item Enrichment Composable
 *
 * Enriches order line items with additional data needed for the return process
 * including shipping status, returnability info, and default values.
 */

/**
 * @typedef {Object} ShippingInfo
 * @property {boolean} inTransit - Whether item is currently in transit
 * @property {boolean} delivered - Whether item has been delivered
 * @property {boolean} stuck - Whether shipment appears to be stuck
 * @property {string} lastUpdated - ISO date string of last tracking update
 * @property {string} [estimatedDelivery] - ISO date string of estimated delivery
 * @property {string} [deliveryDate] - ISO date string of actual delivery
 */

/**
 * @typedef {Object} OrderLineItem
 * @property {string} platformOrderLineItemId - Platform order line item ID
 * @property {number} quantityAvailableToReturn - Quantity available for return
 * @property {boolean} isReturnable - Whether item is returnable
 * @property {Object} [image] - Item image object
 * @property {Object} [shipping] - Shipping information
 * @property {Object} [returnWindow] - Return eligibility window from API
 * @property {boolean} [returnWindow.refund] - Whether refund is available
 * @property {boolean} [returnWindow.inStoreCredit] - Whether store credit is available
 * @property {string} [eligibilityMessage] - Message about return eligibility (refund/credit)
 * @property {string|null} [returnableMessage] - Message if item is not returnable
 */

/**
 * Composable for enriching order items with return-specific data
 * @returns {Object} Enrichment functions
 */
export function useCustomerReturnItemEnrichment() {
  /**
   * Add returnability information to an item based on its returnWindow
   * @param {OrderLineItem} item - The item to enrich
   * @returns {Object} Object with quantityAvailableToReturn, returnableMessage, and eligibilityMessage
   */
  function enrichWithReturnability(item) {
    let qty = item.quantityAvailableToReturn
    let eligibilityMessage

    // Check return window eligibility first (from API per-item data)
    const enforceReturnWindow = true
    const eligibility = getReturnEligibilityMessage(item.returnWindow, {
      enforceReturnWindow,
      returnableFulfillments: item.returnableFulfillments,
    })

    // Check if item is final sale (takes precedence)
    if (item.isReturnable === false) {
      qty = 0
      eligibilityMessage = RETURN_ELIGIBILITY_MESSAGES.FINAL_SALE
    }
    // Check if return window blocks returns
    else if (!eligibility.returnOk) {
      qty = 0
      eligibilityMessage = eligibility.eligibilityMessage
    }
    // Item is returnable, use eligibility message from return window
    else {
      eligibilityMessage = eligibility.eligibilityMessage
    }

    return {
      quantityAvailableToReturn: qty,
      eligibilityMessage,
    }
  }

  /**
   * Set default quantity and selection values for an item
   * @returns {Object} Object with selectedQuantity and selected flag
   */
  function setDefaultQuantities() {
    return {
      selectedQuantity: CUSTOMER_RETURN_CONFIG.item.DEFAULT_SELECTED_QUANTITY,
      selected: false,
    }
  }

  /**
   * Ensure item has a valid image or use default
   * @param {OrderLineItem} item - The item to check
   * @returns {Object} Image object
   */
  function ensureItemImage(item) {
    return item.image || { ...MISSING_IMAGE }
  }

  /**
   * Complete enrichment pipeline for a single line item
   * Processes per-item returnWindow for eligibility checking
   * @param {OrderLineItem} item - The item to enrich
   * @returns {OrderLineItem} Fully enriched item with eligibility info
   */
  function enrichLineItem(item) {
    const returnability = enrichWithReturnability(item)
    const defaults = setDefaultQuantities()
    const image = ensureItemImage(item)

    // can use these on the FE to help direct the customer - they will be on the item
    // fulfillmentStatuses - { DELIVERED: 2, IN_TRANSIT: 1, etc }

    return {
      ...item,
      ...returnability,
      ...defaults,
      image,
    }
  }

  /**
   * Enrich all line items in an order
   * Each item's returnWindow is processed individually for per-item eligibility
   * @param {OrderLineItem[]} lineItems - Array of line items to enrich
   * @returns {OrderLineItem[]} Array of enriched items with per-item eligibility
   */
  function enrichLineItems(lineItems) {
    if (!Array.isArray(lineItems)) {
      return []
    }

    return lineItems.map(item => {
      return enrichLineItem(item)
    })
  }

  return {
    enrichLineItem,
    enrichLineItems,
    enrichWithReturnability,
    ensureItemImage,
    setDefaultQuantities,
  }
}
